import { useEffect, useState } from 'react';
import { parse, startOfDay } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { isTimeoutError } from '@ibk/shared-config';
import { useTranslation } from '@ibk/shared-translation';
import { isValidCep, isValidCnpj, isValidCpf, parseCurrencyToNumber, validateDate } from '@ibk/shared-utils';
import { useGetAddressByCepQuery } from '../../services/addressApi';
import { useCreateBankslipMutation } from '../../services/bankslipApi';
import { BankslipResponseStatus } from '../../types';
export function useChargesCreate({ onBack } = {}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [createBankslip, { isLoading }] = useCreateBankslipMutation();
    const [currentStep, setCurrentStep] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);
    const [createdCharge, setCreatedCharge] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [payerValidationErrors, setPayerValidationErrors] = useState({});
    const [cepToSearch, setCepToSearch] = useState('');
    const [isSearchingCep, setIsSearchingCep] = useState(false);
    const [isTimeoutErrorMessage, setIsTimeoutErrorMessage] = useState(false);
    const { data: addressData, error: addressError, isLoading: isAddressLoading, } = useGetAddressByCepQuery(cepToSearch.length === 8 ? cepToSearch : '', {
        skip: cepToSearch.length !== 8,
    });
    const [chargeInformation, setChargeInformation] = useState({
        amount: '',
        dueDate: '',
        monthlyInterest: '',
        lateFee: '',
        applyDiscount: false,
        discountType: 'percentage',
        discountPercentage: '',
        discountValue: '',
        discountLimitDate: '',
    });
    const [payerInformation, setPayerInformation] = useState({
        name: '',
        document: '',
        cep: '',
        street: '',
        number: '',
        complement: '',
        hasNoNumber: false,
        neighborhood: '',
        city: '',
        state: '',
        description: '',
    });
    const steps = [
        {
            title: t('charges-create.tabs.information'),
        },
        {
            title: t('charges-create.tabs.payer'),
        },
        {
            title: t('charges-create.tabs.generated-billet'),
        },
    ];
    const handleBack = () => {
        if (onBack) {
            onBack();
        }
        else {
            navigate('/conta-pj/receber');
        }
    };
    const handleNextStep = () => {
        if (validateCurrentStep()) {
            if (currentStep < steps.length - 1) {
                setCurrentStep(currentStep + 1);
            }
        }
    };
    const handlePreviousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
        else {
            handleBack();
        }
    };
    const updateChargeInformation = (field, value) => {
        setChargeInformation(prev => ({
            ...prev,
            [field]: value,
        }));
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }
        if (field === 'applyDiscount' && value === false) {
            setChargeInformation(prev => ({
                ...prev,
                discountType: 'percentage',
                discountPercentage: '',
                discountValue: '',
                discountLimitDate: '',
            }));
            setValidationErrors(prev => ({
                ...prev,
                discountPercentage: undefined,
                discountValue: undefined,
                discountLimitDate: undefined,
            }));
        }
        if ((field === 'dueDate' || field === 'discountLimitDate') && typeof value === 'string') {
            const newChargeInfo = { ...chargeInformation, [field]: value };
            if (newChargeInfo.discountLimitDate.trim() &&
                validateDate(newChargeInfo.discountLimitDate) &&
                newChargeInfo.dueDate.trim() &&
                validateDate(newChargeInfo.dueDate)) {
                const parsedDueDate = parse(newChargeInfo.dueDate, 'dd/MM/yyyy', new Date());
                const parsedDiscountLimitDate = parse(newChargeInfo.discountLimitDate, 'dd/MM/yyyy', new Date());
                const dueDateStartOfDay = startOfDay(parsedDueDate);
                const discountLimitDateStartOfDay = startOfDay(parsedDiscountLimitDate);
                if (discountLimitDateStartOfDay > dueDateStartOfDay) {
                    setValidationErrors(prev => ({
                        ...prev,
                        discountLimitDate: t('charges-create.validation.discount-limit-date.greater-than-due-date'),
                    }));
                }
                else {
                    setValidationErrors(prev => ({
                        ...prev,
                        discountLimitDate: undefined,
                    }));
                }
            }
            else if (field === 'discountLimitDate' && validationErrors.discountLimitDate) {
                setValidationErrors(prev => ({
                    ...prev,
                    discountLimitDate: undefined,
                }));
            }
        }
    };
    const updatePayerInformation = (field, value) => {
        setPayerInformation(prev => ({
            ...prev,
            [field]: value,
        }));
        if (payerValidationErrors[field]) {
            setPayerValidationErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }
    };
    const searchCep = (cep) => {
        const cleanCep = cep.replace(/\D/g, '');
        if (cleanCep.length === 8) {
            setCepToSearch(cleanCep);
            setIsSearchingCep(true);
        }
    };
    useEffect(() => {
        if (addressData?.result) {
            const address = addressData.result;
            setPayerInformation(prev => ({
                ...prev,
                state: address.state,
                city: address.city,
                neighborhood: address.neighborhood,
                street: address.street,
            }));
            setIsSearchingCep(false);
            setCepToSearch('');
        }
    }, [addressData]);
    useEffect(() => {
        if (addressError) {
            setErrorMessage(t('charges-create.cep.error') || 'Erro ao buscar CEP. Verifique se o CEP está correto.');
            setIsSearchingCep(false);
            setCepToSearch('');
        }
    }, [addressError, t]);
    const formatDateForApi = (dateString) => {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };
    const cleanDocument = (document) => {
        return document.replace(/\D/g, '');
    };
    const buildDebitorAddress = () => ({
        postalCode: cleanDocument(payerInformation.cep),
        state: payerInformation.state,
        city: payerInformation.city,
        street: payerInformation.street,
        number: payerInformation.hasNoNumber ? '0' : payerInformation.number,
        neighborhood: payerInformation.neighborhood,
        complement: payerInformation.complement || undefined,
    });
    const buildDiscountInstruction = () => {
        const discountAmount = chargeInformation.discountType === 'percentage'
            ? parseFloat(chargeInformation.discountPercentage)
            : parseCurrencyToNumber(chargeInformation.discountValue);
        return {
            amount: discountAmount,
            limitDate: formatDateForApi(chargeInformation.discountLimitDate),
            modality: chargeInformation.discountType === 'percentage' ? 'percent' : 'fixed',
        };
    };
    const buildInstructions = () => {
        const instructions = {};
        if (chargeInformation.monthlyInterest) {
            instructions.interest = parseFloat(chargeInformation.monthlyInterest);
        }
        if (chargeInformation.lateFee) {
            instructions.fine = parseFloat(chargeInformation.lateFee);
        }
        if (chargeInformation.applyDiscount) {
            instructions.discount = buildDiscountInstruction();
        }
        return instructions;
    };
    const createCharge = async () => {
        try {
            setErrorMessage(null);
            if (!validateCurrentStep()) {
                return;
            }
            const payload = {
                amount: parseCurrencyToNumber(chargeInformation.amount),
                debitor: {
                    name: payerInformation.name,
                    document: cleanDocument(payerInformation.document),
                    address: buildDebitorAddress(),
                },
                duedate: formatDateForApi(chargeInformation.dueDate),
                description: payerInformation.description || 'Cobrança',
                bankEmissor: 1,
                instructions: buildInstructions(),
            };
            const response = await createBankslip(payload).unwrap();
            if (!response.result) {
                throw new Error(t('charges-create.error'));
            }
            const isSuccessResponseStatus = response.status === 200 || response.status === 201;
            if (isSuccessResponseStatus && response.result.status === BankslipResponseStatus.PENDING) {
                setIsTimeoutErrorMessage(true);
                return;
            }
            if (isSuccessResponseStatus && response.result.status !== BankslipResponseStatus.PENDING_PAYMENT) {
                throw new Error(t('charges-create.error'));
            }
            setCreatedCharge(response.result);
            setCurrentStep(2);
        }
        catch (error) {
            const isTimeoutErrorValue = isTimeoutError(error);
            if (isTimeoutErrorValue) {
                setIsTimeoutErrorMessage(true);
            }
            else {
                setErrorMessage(t('charges-create.error'));
            }
        }
    };
    const validateAmount = (errors) => {
        if (!chargeInformation.amount.trim()) {
            errors.amount = t('charges-create.validation.amount.required');
            return;
        }
        const amountValue = parseCurrencyToNumber(chargeInformation.amount);
        if (amountValue < 5) {
            errors.amount = t('charges-create.validation.amount.min');
        }
    };
    const validateDueDate = (errors) => {
        if (!chargeInformation.dueDate.trim()) {
            errors.dueDate = t('charges-create.validation.due-date.required');
            return;
        }
        if (!validateDate(chargeInformation.dueDate)) {
            errors.dueDate = t('charges-create.validation.due-date.invalid');
            return;
        }
        const parsedDueDate = parse(chargeInformation.dueDate, 'dd/MM/yyyy', new Date());
        const today = startOfDay(new Date());
        const dueDateStartOfDay = startOfDay(parsedDueDate);
        if (dueDateStartOfDay < today) {
            errors.dueDate = t('charges-create.validation.due-date.past');
        }
    };
    const validateDiscountFields = (errors) => {
        if (!chargeInformation.applyDiscount) {
            return;
        }
        if (chargeInformation.discountType === 'percentage') {
            if (!chargeInformation.discountPercentage.trim()) {
                errors.discountPercentage = t('charges-create.validation.discount-percentage.required');
            }
        }
        else if (!chargeInformation.discountValue.trim()) {
            errors.discountValue = t('charges-create.validation.discount-value.required');
        }
        if (!chargeInformation.discountLimitDate.trim()) {
            errors.discountLimitDate = t('charges-create.validation.discount-limit-date.required');
        }
        else if (!validateDate(chargeInformation.discountLimitDate)) {
            errors.discountLimitDate = t('charges-create.validation.discount-limit-date.invalid');
        }
        else {
            if (chargeInformation.dueDate.trim() && validateDate(chargeInformation.dueDate)) {
                const parsedDueDate = parse(chargeInformation.dueDate, 'dd/MM/yyyy', new Date());
                const parsedDiscountLimitDate = parse(chargeInformation.discountLimitDate, 'dd/MM/yyyy', new Date());
                const dueDateStartOfDay = startOfDay(parsedDueDate);
                const discountLimitDateStartOfDay = startOfDay(parsedDiscountLimitDate);
                if (discountLimitDateStartOfDay > dueDateStartOfDay) {
                    errors.discountLimitDate = t('charges-create.validation.discount-limit-date.greater-than-due-date');
                }
            }
        }
    };
    const validatePayerName = (errors) => {
        if (!payerInformation.name.trim()) {
            errors.name = t('charges-create.payer.validation.name.required');
        }
    };
    const validatePayerDocument = (errors) => {
        if (!payerInformation.document.trim()) {
            errors.document = t('charges-create.payer.validation.document.required');
            return;
        }
        const cleanDocument = payerInformation.document.replace(/\D/g, '');
        const isValid = cleanDocument.length === 11 ? isValidCpf(payerInformation.document) : isValidCnpj(payerInformation.document);
        if (!isValid) {
            errors.document = t('charges-create.payer.validation.document.invalid');
        }
    };
    const validatePayerCep = (errors) => {
        if (!payerInformation.cep.trim()) {
            errors.cep = t('charges-create.payer.validation.cep.required');
            return;
        }
        if (!isValidCep(payerInformation.cep)) {
            errors.cep = t('charges-create.payer.validation.cep.invalid');
        }
    };
    const validatePayerAddress = (errors) => {
        if (!payerInformation.street.trim()) {
            errors.street = t('charges-create.payer.validation.street.required');
        }
        if (!payerInformation.hasNoNumber && !payerInformation.number.trim()) {
            errors.number = t('charges-create.payer.validation.number.required');
        }
        if (!payerInformation.neighborhood.trim()) {
            errors.neighborhood = t('charges-create.payer.validation.neighborhood.required');
        }
        if (!payerInformation.city.trim()) {
            errors.city = t('charges-create.payer.validation.city.required');
        }
        if (!payerInformation.state.trim()) {
            errors.state = t('charges-create.payer.validation.state.required');
        }
    };
    const validateCurrentStep = () => {
        if (currentStep === 0) {
            const errors = {};
            validateAmount(errors);
            validateDueDate(errors);
            validateDiscountFields(errors);
            setValidationErrors(errors);
            return Object.keys(errors).length === 0;
        }
        else if (currentStep === 1) {
            const errors = {};
            validatePayerName(errors);
            validatePayerDocument(errors);
            validatePayerCep(errors);
            validatePayerAddress(errors);
            setPayerValidationErrors(errors);
            return Object.keys(errors).length === 0;
        }
        return true;
    };
    const breadcrumbItems = [
        {
            label: t('charges-create.breadcrumb.to-receive'),
            onClick: handleBack,
            isActive: false,
        },
        {
            label: t('charges-create.breadcrumb.create-charge'),
            isActive: true,
        },
    ];
    return {
        currentStep,
        errorMessage,
        steps,
        breadcrumbItems,
        chargeInformation,
        payerInformation,
        updateChargeInformation,
        updatePayerInformation,
        validationErrors,
        payerValidationErrors,
        validateCurrentStep,
        handleBack,
        handleNextStep,
        handlePreviousStep,
        setErrorMessage,
        createCharge,
        isLoading: isLoading || isAddressLoading,
        createdCharge,
        searchCep,
        isSearchingCep,
        isTimeoutErrorMessage,
        setIsTimeoutErrorMessage,
    };
}
